import { useEffect, useState } from "react";
import { Col, Row, Button, Card, Form, Container } from "react-bootstrap";
import swal from "sweetalert";
import useApiServices from "../../../utils/useApiServices";
import {
  FETCH_ALL_MERCHANT_VALIDATIONS,
  FETCH_MERCHANT_SERVICES,
  SAVE_MERCHANT_IP,
  UPDATE_MERCHANT_IP,
} from "../../../utils/constant";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import DownloadIcon from "@mui/icons-material/Download";

import { ServiceIcons } from "../../../Assets/images/image";

const IPManagement = ({ toggleRightBar }) => {
  const [merchantData, setMerchantData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [mid, setMid] = useState("");
  const [services, setServices] = useState([]);
  const [serviceIPs, setServiceIPs] = useState({});
  const [editMode, setEditMode] = useState({});
  const [originalIPCount, setOriginalIPCount] = useState({});
  const [noServices, setNoServices] = useState(false);

  const hasPrivilage = true;
  const { apiCallFnforGet, apiCallFnforPost, apiCallFnforPut } =
    useApiServices();

  // simple IPv4 regex (allows 0-255 in each octet)
  const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

  // Fetch all merchants
  const getMerchantData = async () => {
    try {
      const response = await apiCallFnforGet(FETCH_ALL_MERCHANT_VALIDATIONS);
      if (response?.statusCode === 200) setMerchantData(response?.data);
    } catch (err) {
      // optionally handle
    }
  };

  useEffect(() => {
    getMerchantData();
  }, []);

  // tooltips on elements with data-bs-toggle="tooltip"
  useEffect(() => {
    try {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );

      // First destroy all existing tooltips
      tooltipTriggerList.forEach((el) => {
        if (el._tooltipInstance) {
          el._tooltipInstance.dispose();
        }
      });

      // Then initialize tooltip again (clean initialization)
      tooltipTriggerList.forEach((el) => {
        if (window.bootstrap) {
          el._tooltipInstance = new window.bootstrap.Tooltip(el);
        }
      });
    } catch (e) { }
  }, [services, serviceIPs]);

  // Fetch services for selected merchant
  const getMerchantServices = async () => {
    if (!mid) {
      setServices([]);
      setServiceIPs({});
      setOriginalIPCount({});
      setEditMode({});
      setNoServices(false);
      return;
    }

    try {
      const res = await apiCallFnforGet(
        `${FETCH_MERCHANT_SERVICES}?mid=${mid}`
      );

      if (
        res?.statusCode === 200 &&
        Array.isArray(res?.data?.services) &&
        res.data.services.length > 0
      ) {
        const serviceList = res.data.services
          .map((svc) => ({
            serviceId: svc?.serviceId,
            serviceName: svc?.serviceName,
          }))
          .sort((a, b) => a?.serviceId - b?.serviceId);
        setServices(serviceList);

        const ipInit = {};
        const origCount = {};
        res.data.services.forEach((svc) => {
          const existingIPs =
            Array.isArray(svc.ipList) && svc.ipList.length > 0
              ? svc.ipList
              : [""];
          ipInit[svc.serviceId] = existingIPs.map((ip) => ({
            value: ip,
            saved: ip !== "",
          }));
          origCount[svc.serviceId] = svc.hasOwnProperty("ipList")
            ? svc.ipList.length
            : null;
        });

        setServiceIPs(ipInit);
        setOriginalIPCount(origCount);
        setEditMode({});
        setNoServices(false);
      } else {
        // No services found
        setServices([]);
        setServiceIPs({});
        setOriginalIPCount({});
        setEditMode({});
        setNoServices(true);
      }
    } catch (err) {
      swal("Something went wrong", "", "error");
      setServices([]);
      setServiceIPs({});
      setOriginalIPCount({});
      setEditMode({});
      setNoServices(true);
    }
  };

  useEffect(() => {
    getMerchantServices();
  }, [mid]);

  // Merchant selection
  const handleCompanyChange = (e) => {
    setSelectedCompany(e.target.value);
    const selected = merchantData.find(
      (m) => `${m.prifix} :- ${m.mid} ${m.merchantName}` === e.target.value
    );
    setMid(selected ? selected.mid : "");
  };

  // Add new IP field (max 4)
  const addIPField = (serviceId) => {
    if ((serviceIPs[serviceId] || []).length >= 4) {
      swal("Only 4 IPs allowed per service", "", "warning");
      return;
    }

    setServiceIPs((prev) => ({
      ...prev,
      [serviceId]: [...(prev[serviceId] || []), { value: "", saved: false }],
    }));
    setEditMode((prev) => ({ ...prev, [serviceId]: true }));
  };

  // Remove IP with confirm
  const removeIPField = async (serviceId, index) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: "Do you really want to delete this IP?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!willDelete) return;

    const updated = serviceIPs[serviceId].filter((_, i) => i !== index);
    setServiceIPs((prev) => ({
      ...prev,
      [serviceId]: updated,
    }));

    const deletedWasSaved = serviceIPs[serviceId][index].saved;
    if (!deletedWasSaved) return;

    const payload = { mid, serviceId, ipList: updated.map((i) => i.value) };
    const res = await apiCallFnforPut(UPDATE_MERCHANT_IP, payload);
    if (res?.statusCode === 200)
      swal("Success", "IP removed successfully", "success");
  };

  // Handle IP change
  const handleIPChange = (serviceId, index, value) => {
    setServiceIPs((prev) => ({
      ...prev,
      [serviceId]: prev[serviceId].map((ip, i) =>
        i === index ? { ...ip, value, saved: false } : ip
      ),
    }));
    setEditMode((prev) => ({ ...prev, [serviceId]: true }));
  };

  const enableEdit = (serviceId) => {
    setEditMode((prev) => ({ ...prev, [serviceId]: true }));
  };

  // Validate all IPs for a service
  const validateIPs = (ipArray) => {
    for (let ipObj of ipArray) {
      if (!ipObj.value || !ipv4Regex.test(ipObj.value.trim())) return false;
    }
    return true;
  };

  // Save or update IPs
  const saveOrUpdate = async (serviceId) => {
    const ipData = serviceIPs[serviceId] || [];

    if (ipData.length === 0) {
      swal("No IPs to save", "", "warning");
      return;
    }

    if (!validateIPs(ipData)) {
      swal(
        "Invalid IP format detected",
        "Valid IP Format: 192.168.1.100",
        "warning"
      );
      return;
    }

    const payload = { mid, serviceId, ipList: ipData.map((i) => i.value) };
    const isFirstTime = originalIPCount[serviceId] === null;

    const apiRes = isFirstTime
      ? await apiCallFnforPost(SAVE_MERCHANT_IP, payload)
      : await apiCallFnforPut(UPDATE_MERCHANT_IP, payload);

    if (apiRes?.statusCode === 200) {
      swal(
        "Success",
        isFirstTime ? "Saved Successfully" : "Updated Successfully",
        "success"
      );

      setOriginalIPCount((prev) => ({ ...prev, [serviceId]: ipData.length }));
      setServiceIPs((prev) => ({
        ...prev,
        [serviceId]: ipData.map((i) => ({ ...i, saved: true })),
      }));
      setEditMode((prev) => ({ ...prev, [serviceId]: false }));
    } else {
      swal("Error", apiRes?.message || "Something went wrong", "error");
    }
  };

  // Export CSV for current merchant
  const exportCSV = () => {
    if (!mid) {
      swal("Please select a merchant first", "", "warning");
      return;
    }

    const selected = merchantData.find((m) => m.mid === mid);

    let rows = [["Merchant Name", "MID", "Service Name", "IP Address"]];

    services.forEach((svc) => {
      (serviceIPs[svc.serviceId] || []).forEach((ip) => {
        rows.push([
          selected?.merchantName || "",
          selected?.mid || "",
          svc.serviceName,
          ip.value || "",
        ]);
      });
    });

    let csvContent =
      "data:text/csv;charset=utf-8," +
      rows
        .map((e) =>
          e
            .map(
              (cell) => `\"${(cell || "").toString().replace(/\"/g, '""')}\"`
            )
            .join(",")
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `merchant_${mid || "all"}_full_ip_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container-fluid p-3 min-vh-100">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded shadow-sm">
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn-close me-3"
            onClick={toggleRightBar}
            aria-label="Close"
          ></button>
          <div className="fs-3 fw-bold text-dark">IP Whitelisting Management</div>
        </div>
        <Button
          variant="dark"
          onClick={exportCSV}
          disabled={!mid}
          className="d-flex align-items-center"
        >
          <DownloadIcon fontSize="small" className="me-2" /> Export CSV
        </Button>
      </div>

      {/* Merchant Selection */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={6}>
              <label className="d-flex text-dark fs-4">
                Select A Merchant
              </label>
              <select
                className="form-control"
                onChange={handleCompanyChange}
                value={selectedCompany}
              >
                <option value="">Select Merchant</option>
                {merchantData?.map((m) => (
                  <option
                    key={m?.mid}
                    value={`${m?.prifix} :- ${m?.mid} ${m?.merchantName}`}
                  >
                    {m?.prifix} :- {m?.mid} - {m?.merchantName}
                  </option>
                ))}
              </select>
            </Col>
            <Col md={6}>
              <label className="d-flex text-dark fs-4">Merchant MID</label>
              <input className="form-control" value={mid} readOnly />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Content Area */}
      {
        !mid ? (
          <div className="text-center py-5">
            <h4 className="text-muted">Please select a merchant to manage IPs</h4>
          </div>
        ) : (
          <>
            {noServices ? (
              <div className="alert alert-warning text-center p-4 rounded shadow-sm" role="alert">
                <h4 className="alert-heading">No Services Found</h4>
                <p>There are no services configured for the selected merchant.</p>
              </div>
            ) : (
              <Row>
                {services.map((svc) => (
                  <Col lg={6} xl={4} key={svc.serviceId} className="mb-4">
                    <Card className="h-100 shadow-sm border-0 service-card">
                      <Card.Header className="bg-white border-bottom-0 pt-4 pb-2 d-flex align-items-center">
                        <div className="p-2 bg-light rounded me-3 border">
                          <img
                            src={
                              ServiceIcons[svc?.serviceName] ||
                              ServiceIcons?.DEFAULT
                            }
                            alt={`${svc?.serviceName}`}
                            style={{ width: 40, height: 40, objectFit: "contain" }}
                          />
                        </div>
                        <h5 className="fw-bold mb-0 text-primary">{svc.serviceName}</h5>
                      </Card.Header>
                      <Card.Body>
                        <p className="text-muted small mb-3">
                          Allowed IPs (Max 4). Enter valid IPv4 addresses.
                        </p>

                        <div className="d-flex flex-column gap-3">
                          {(serviceIPs[svc.serviceId] || []).map((ipObj, idx) => (
                            <div key={idx} className="d-flex align-items-center">
                              <Form.Control
                                type="text"
                                value={ipObj.value}
                                onChange={(e) =>
                                  handleIPChange(svc.serviceId, idx, e.target.value)
                                }
                                placeholder="e.g. 192.168.1.100"
                                title="Valid IP Format: 192.168.1.100"
                                readOnly={!hasPrivilage}
                                className={`me-2 ${!ipv4Regex.test(ipObj.value) && ipObj.value ? "is-invalid" : ""}`}
                              />
                              {hasPrivilage && (
                                <Button
                                  variant="light"
                                  className="text-danger p-2 border-0"
                                  onClick={() => removeIPField(svc.serviceId, idx)}
                                  title="Remove IP"
                                >
                                  <DeleteOutlineIcon />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>

                        {hasPrivilage && (serviceIPs[svc.serviceId] || []).length < 4 && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="w-100 mt-3 border-dashed"
                            onClick={() => addIPField(svc.serviceId)}
                            style={{ borderStyle: "dashed" }}
                          >
                            <AddCircleOutlineIcon fontSize="small" className="me-1" /> Add IP Address
                          </Button>
                        )}
                      </Card.Body>
                      <Card.Footer className="bg-white border-top-0 pb-4 pt-0">
                        {hasPrivilage && (
                          <Button
                            variant="primary"
                            className="w-100 d-flex justify-content-center align-items-center"
                            onClick={() => saveOrUpdate(svc.serviceId)}
                          >
                            {originalIPCount[svc.serviceId] === null ? (
                              <>
                                <SaveIcon fontSize="small" className="me-2" /> Save Configuration
                              </>
                            ) : (
                              <>
                                <UpdateIcon fontSize="small" className="me-2" /> Update Configuration
                              </>
                            )}
                          </Button>
                        )}
                      </Card.Footer>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )
      }
    </div >
  );
};

export default IPManagement;
